import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import conversations, { ChatStats } from "../../services/conversations";
import Skeleton from "../ui/Skeleton";

export const EmptyChatState: React.FC = () => {
  const [stats, setStats] = useState<ChatStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState<7 | 15 | 30>(7);

  useEffect(() => {
    async function fetchStats() {
      setLoading(true);
      try {
        const data = await conversations.getChatStats(days);
        setStats(data);
      } catch (error) {
        console.error("Erro ao carregar stats", error);
      } finally {
        setLoading(false);
      }
    }
    fetchStats();
  }, [days]);

  if (loading) {
    return (
      <div className="flex-1 flex flex-col p-4 overflow-y-auto bg-gray-50 dark:bg-gray-900 h-full md:p-6">
        {/* Title Skeleton */}
        <Skeleton className="h-7 w-48 mb-4 rounded-lg md:h-8 md:w-64 md:mb-6" />

        {/* KPI Cards Skeleton */}
        <div className="grid grid-cols-1 gap-3 mb-6 md:grid-cols-2 md:gap-4 md:mb-8 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 h-28 flex flex-col justify-between relative md:p-6 md:h-32"
            >
              <div className="absolute top-0 right-0 p-2.5 md:p-3">
                <Skeleton className="w-2 h-2 rounded-full" />
              </div>
              <Skeleton className="h-3.5 w-16 rounded md:h-4 md:w-20" />
              <div className="flex items-end gap-2">
                <Skeleton className="h-7 w-14 rounded md:h-8 md:w-16" />
                <Skeleton className="h-3 w-7 mb-1 rounded md:w-8" />
              </div>
            </div>
          ))}
        </div>

        {/* Chart Skeleton */}
        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 flex-1 min-h-[300px] flex flex-col md:p-6 md:min-h-[400px]">
          <div className="flex flex-col justify-between mb-4 gap-3 md:flex-row md:items-center md:mb-6 md:gap-4">
            <Skeleton className="h-5 w-40 rounded md:h-6 md:w-48" />
            <div className="flex gap-2 self-start md:self-auto">
              <Skeleton className="h-8 w-14 rounded-md md:w-16" />
              <Skeleton className="h-8 w-14 rounded-md md:w-16" />
              <Skeleton className="h-8 w-14 rounded-md md:w-16" />
            </div>
          </div>
          <div className="flex-1 w-full min-h-0">
            <Skeleton className="w-full h-full rounded-lg" />
          </div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900 px-4">
        <div className="w-48 h-48 mb-4 opacity-20 md:w-64 md:h-64 md:mb-6">
          <svg viewBox="0 0 303 172" fill="currentColor">
            <path d="M151.5 0C67.9 0 0 67.9 0 151.5S67.9 303 151.5 303 303 235.1 303 151.5 235.1 0 151.5 0zm0 284.3c-73.2 0-132.8-59.6-132.8-132.8S78.3 18.7 151.5 18.7 284.3 78.3 284.3 151.5 224.7 284.3 151.5 284.3z" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold mb-2 md:text-2xl">Minhas Conversas</h2>
        <p className="text-center max-w-sm text-[13px] px-4 md:max-w-md md:text-xs md:px-0">
          Selecione uma conversa ao lado para começar a trocar mensagens com os
          alunos
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col p-4 overflow-y-auto bg-gray-50 dark:bg-gray-900 h-full md:p-6">
      <h2 className="text-xl font-bold mb-4 text-gray-800 dark:text-white md:text-2xl md:mb-6">
        Dashboard de Mensagens
      </h2>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 gap-3 mb-6 md:grid-cols-2 md:gap-4 md:mb-8 lg:grid-cols-4">
        <KpiCard
          title="Enviadas"
          value={stats.kpis.total_sent}
          textColor="text-blue-500"
          dotColor="bg-blue-500"
          bgColor="bg-blue-50 dark:bg-blue-900/20"
        />
        <KpiCard
          title="Recebidas"
          value={stats.kpis.total_received}
          textColor="text-green-500"
          dotColor="bg-green-500"
          bgColor="bg-green-50 dark:bg-green-900/20"
        />
        <KpiCard
          title="Entregues"
          value={stats.kpis.delivered}
          textColor="text-orange-500"
          dotColor="bg-orange-500"
          bgColor="bg-orange-50 dark:bg-orange-900/20"
        />
        <KpiCard
          title="Lidas"
          value={stats.kpis.read}
          textColor="text-purple-500"
          dotColor="bg-purple-500"
          bgColor="bg-purple-50 dark:bg-purple-900/20"
        />
      </div>

      {/* Chart */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 flex-1 min-h-[300px] flex flex-col md:p-6 md:min-h-[400px]">
        <div className="flex flex-col justify-between mb-4 gap-3 md:flex-row md:items-center md:mb-6 md:gap-4">
          <h3 className="text-base font-semibold text-gray-700 dark:text-gray-200 md:text-lg">
            Atividade Recente ({days} dias)
          </h3>
          <div className="flex bg-gray-100 dark:bg-gray-700 p-1 rounded-lg self-start md:self-auto">
            {[7, 15, 30].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d as 7 | 15 | 30)}
                className={`px-2.5 py-1 text-[13px] font-medium rounded-md transition-colors active:scale-95 md:px-3 md:text-sm ${
                  days === d
                    ? "bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm"
                    : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                }`}
              >
                {d} dias
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 w-full min-h-0">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={stats.chart_data}
              margin={{
                top: 10,
                right: 10,
                left: 0,
                bottom: 5,
              }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                opacity={0.1}
                vertical={false}
              />
              <XAxis
                dataKey="date"
                stroke="#888888"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                dy={8}
              />
              <YAxis
                stroke="#888888"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => `${value}`}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                  borderRadius: "8px",
                  border: "none",
                  boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                  color: "#333",
                  fontSize: "12px",
                }}
                cursor={{ fill: "transparent" }}
              />
              <Legend wrapperStyle={{ paddingTop: "12px", fontSize: "12px" }} />
              <Bar
                dataKey="enviadas"
                name="Enviadas"
                fill="#9333ea"
                radius={[4, 4, 0, 0]}
                barSize={24}
              />
              <Bar
                dataKey="recebidas"
                name="Recebidas"
                fill="#85368c"
                radius={[4, 4, 0, 0]}
                barSize={24}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

const KpiCard = ({
  title,
  value,
  textColor,
  dotColor,
  bgColor,
}: {
  title: string;
  value: number;
  textColor: string;
  dotColor: string;
  bgColor: string;
}) => (
  <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 flex flex-col justify-between h-28 relative overflow-hidden group hover:shadow-md transition-shadow active:scale-[0.98] md:p-6 md:h-32">
    <div className={`absolute top-0 right-0 p-2.5 rounded-bl-xl ${bgColor} md:p-3`}>
      <div className={`w-2 h-2 rounded-full ${dotColor}`}></div>
    </div>
    <span className="text-[13px] font-medium text-gray-500 dark:text-gray-400 md:text-sm">
      {title}
    </span>
    <div className="flex items-end gap-1.5 md:gap-2">
      <span className={`text-2xl font-bold ${textColor} md:text-3xl`}>{value}</span>
      <span className="text-[11px] text-gray-400 mb-0.5 md:text-xs md:mb-1">msgs</span>
    </div>
  </div>
);
